// Dashboard page (real data view) — orchestrator only.
//
// Per the "Thinking in React" rule (see .claude/rules/thinking-in-react.md):
//
// 1. Hierarchy:
//      DashboardReal
//        ├── useTopProducts(isBuyer ? null : date)           — SWR
//        ├── useDeliveryStats(isBuyer ? null : date)         — SWR
//        ├── <KpiRow amounts={...} />
//        └── content grid
//            ├── left:  <SalesChartCard>, <RecentOrdersCard>, <PartnerBreakdownCard>
//            └── right: <OrderStatusCard>, <TopProductsCard>, <DeliveryPerformanceCard>
//
// 2. Static: every card is fed purely via props; no card owns state.
// 3. Minimal state: this view owns ZERO state — filter / startDate /
//    endDate live in the parent page (pages/dashboard/index.tsx) and
//    arrive through props. The two SWR fetches are derived from date.
// 4. State location: parent (parent page) owns filter+dates because
//    the MonthPickerInput at the page header drives both KPI data and
//    the chart range. Pulling them down here would require lifting
//    later anyway.
// 5. Inverse data flow: SalesChartCard pushes start/end date changes
//    back via onStartDateChange / onEndDateChange; PartnerBreakdownCard
//    pushes filter changes via onFilterChange; both view-all anchors
//    receive onViewAll closures built once at this orchestration layer.

import { Box, Text } from "@mantine/core"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { IDashboard } from "../../models/Dashboard"
import { useDeliveryStats, useTopProducts } from "./hooks"
import { useIsBuyer } from "./use-portal"
import { DeliveryPerformanceCard } from "./parts/DeliveryPerformanceCard"
import { BuyerActionsRow } from "./parts/BuyerActionsRow"
import { BuyerLastOrder } from "./parts/BuyerLastOrder"
import { KpiRow } from "./parts/KpiRow"
import { OrderStatusCard } from "./parts/OrderStatusCard"
import { PartnerBreakdownCard } from "./parts/PartnerBreakdownCard"
import { RecentOrdersCard } from "./parts/RecentOrdersCard"
import { SalesChartCard } from "./parts/SalesChartCard"
import { TopProductsCard } from "./parts/TopProductsCard"
import classes from "./real.module.css"

interface Props {
  data?: IDashboard | null
  date: string | null
  filter: string | null
  setFilter: (next: string | null) => void
  startDate: string | null
  endDate: string | null
  setStartDate: (next: string | null) => void
  setEndDate: (next: string | null) => void
}

export function DashboardReal({
  data,
  date,
  filter,
  setFilter,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
}: Props) {
  const navigate = useNavigate()
  const isBuyer = useIsBuyer()
  const { t } = useTranslation(["dashboard"])

  const topProducts = useTopProducts(isBuyer ? null : date)
  const { deliveryRate, avgDeliveryDays } = useDeliveryStats(isBuyer ? null : date)

  // Portal decides the "View all" target, whose counterparty each row shows,
  // and whether a figure is called sales or spend. Resolved once here and
  // threaded down so no card reads Redux for itself.
  const onViewAllSales = () => {
    navigate(isBuyer ? "/buyer-sales" : "/supplier-sales")
  }

  // Canvas OrdB_Home: KPIs, the four answer cards, last order + supplier, promotions.
  // None of the supplier charts (sales, status, Top 5, donut, delivery performance) belong here.
  if (isBuyer) {
    return (
      <Box className={classes.root}>
        <Text component="h2" className={classes.buyerSection} m={0}>
          {t("dashboard:buyerActions.overview")}
          {date ? <Text span className={classes.buyerSectionSub}>{dayjs(date).format(t("dashboard:dateFormat"))}</Text> : null}
        </Text>
        <KpiRow amounts={data?.amounts} isBuyer />
        <BuyerActionsRow />
        <BuyerLastOrder />
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      <KpiRow amounts={data?.amounts} isBuyer={isBuyer} />

      <Box className={classes.contentGrid}>
        <Box className={classes.contentLeft}>
          <SalesChartCard
            byInterval={data?.byInterval}
            isBuyer={isBuyer}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <RecentOrdersCard
            recentDeliveries={data?.recentDeliveries}
            isBuyer={isBuyer}
            onViewAll={onViewAllSales}
          />
          <PartnerBreakdownCard
            byPie={data?.byPie}
            isBuyer={isBuyer}
            filter={filter}
            onFilterChange={setFilter}
          />
        </Box>

        <Box className={classes.contentRight}>
          <OrderStatusCard byStatus={data?.byStatus} onViewAll={onViewAllSales} />
          <TopProductsCard products={topProducts} />
          <DeliveryPerformanceCard
            deliveryRate={deliveryRate}
            avgDeliveryDays={avgDeliveryDays}
          />
        </Box>
      </Box>
    </Box>
  )
}
